<template>
  <div class="container pb-10">
    <div class="flex items-center mb-10">
      <a href="/admin/forms/request-licensing" class="text-gray-900 no-underline">
        <d-icon icon="arrow-left" size="1x" class="mr-3" />
        <span class="text-xl">Back To Listing Page</span>
      </a>
    </div>

    <h2>Company Information</h2>
    <view-organization-details :organization.sync="computedSubmissionDetails.revisedCompany" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Additional Company Information</h2>
    <div class="flex -mx-5 relative">
      <div class="w-1/2 px-5">
        <div class="mb-3">
          <p class="font-bold">
            Quality Grade
          </p>
          <p v-text="computedSubmissionDetails.revisedQualityGrade" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            License Type
          </p>
          <p v-text="computedSubmissionDetails.revisedLicenseType" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Licensing Sections
          </p>
          <p>
            {{ computedLicensingSections.join(', ') }}
          </p>
        </div>
        <div class="mb-3">
          <p class="font-bold">
            How did you hear about the QCP?
          </p>
          <p v-text="computedSubmissionDetails.revisedHowDidYouHearAboutQCP" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            AWI Standards
          </p>
          <ul class="list-disc list-inside">
            <li v-for="standard in computedStandards" :key="standard" v-text="standard" />
          </ul>
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Line of Business
          </p>
          <ul class="list-disc list-inside">
            <li v-for="text in computedLineOfBusiness" :key="text" v-text="text" />
          </ul>
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Join Reason
          </p>
          <p v-text="computedSubmissionDetails.revisedJoinReason" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Business Summary
          </p>
          <p v-text="computedSubmissionDetails.revisedBusinessSummary" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Additionally Selected Information:
          </p>
          <ul class="list-disc list-inside">
            <li v-if="computedSubmissionDetails.revisedCompanyActivelySeeksAndConductsBusienss">
              This company actively seeks and conducts business with the general public (Policy Requirement)
            </li>
            <li v-if="computedSubmissionDetails.revisedRecentlyBidAProjectRequiringQCP">
              We have recently bid a project requiring QCP certification.
            </li>
            <li v-if="computedSubmissionDetails.revisedHaveBeenAwardedContractRequiringQCP">
              We have been awarded a contract for a project requiring QCP certification.
            </li>
          </ul>
        </div>
      </div>

      <div class="w-1/2 px-5">
        <div class="mb-3">
          <p class="font-bold">
            Years in Business
          </p>
          <p v-text="computedSubmissionDetails.revisedYearsInBusiness" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Total Sq. Footage
          </p>
          <p v-text="computedSubmissionDetails.revisedTotalSquareFootage" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            # Installers
          </p>
          <p v-text="computedSubmissionDetails.revisedNumberOfInstallers" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            # Employees in Office
          </p>
          <p v-text="computedSubmissionDetails.revisedNumberOfEmployeesInOffice" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            # of Employees
          </p>
          <p v-text="computedSubmissionDetails.revisedNumberOfEmployees" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Annual Gross Sales
          </p>
          <p v-text="computedSubmissionDetails.revisedAnnualGrossSales" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Own or Lease Facility?
          </p>
          <p v-text="computedSubmissionDetails.revisedOwnOrLeaseFacility" />
        </div>
        <div class="mb-3">
          <p class="font-bold">
            Own or Lease Equipment?
          </p>
          <p v-text="computedSubmissionDetails.revisedOwnOrLeaseEquipment" />
        </div>
      </div>
    </div>

    <hr>

    <h2>Transaction Details</h2>
    <div class="flex -mx-5 relative">
      <div class="w-1/2 px-5">
        <div>
          <div class="mb-3">
            <p class="font-bold">
              Amount
            </p>
            <p>${{ computedTransactionDetails.amount.toFixed(2) }}</p>
          </div>
          <div v-if="computedTransactionDetails.companyName" class="mb-3">
            <p class="font-bold">
              Company Name
            </p>
            <p v-text="computedTransactionDetails.companyName" />
          </div>
          <div v-if="computedTransactionDetails.billingAddress1" class="mb-3">
            <p class="font-bold">
              Billing Address 1
            </p>
            <p v-text="computedTransactionDetails.billingAddress1" />
          </div>
          <div v-if="computedTransactionDetails.billingAddress2" class="mb-3">
            <p class="font-bold">
              Billing Address 2
            </p>
            <p v-text="computedTransactionDetails.billingAddress2" />
          </div>
          <div v-if="computedTransactionDetails.billingCity" class="mb-3">
            <p class="font-bold">
              Billing City
            </p>
            <p v-text="computedTransactionDetails.billingCity" />
          </div>
          <div v-if="computedTransactionDetails.billingStateProvince" class="mb-3">
            <p class="font-bold">
              Billing State
            </p>
            <p v-text="computedTransactionDetails.billingStateProvince" />
          </div>
          <div v-if="computedTransactionDetails.billingZip" class="mb-3">
            <p class="font-bold">
              Billing Zip
            </p>
            <p v-text="computedTransactionDetails.billingZip" />
          </div>
          <div v-if="computedTransactionDetails.billingEmail" class="mb-3">
            <p class="font-bold">
              Billing Email
            </p>
            <p v-text="computedTransactionDetails.billingEmail" />
          </div>
          <div v-if="computedTransactionDetails.billingPhone" class="mb-3">
            <p class="font-bold">
              Billing Phone
            </p>
            <p v-text="computedTransactionDetails.billingPhone" />
          </div>
        </div>
      </div>
    </div>

    <hr>

    <h2>Primary Contact Information</h2>
    <view-contact-details :contact.sync="computedSubmissionDetails.revisedPrimaryContact" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Principal Contact #1</h2>
    <view-contact-details :contact.sync="computedSubmissionDetails.revisedPrincipal1Contact" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Principal Contact #2</h2>
    <view-contact-details :contact.sync="computedSubmissionDetails.revisedPrincipal2Contact" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Project #1</h2>
    <view-project-details :project.sync="computedSubmissionDetails.revisedProject1" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Project #2</h2>
    <view-project-details :project.sync="computedSubmissionDetails.revisedProject2" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />

    <hr>

    <h2>Project #3</h2>
    <view-project-details :project.sync="computedSubmissionDetails.revisedProject3" :submission-source="SubmissionSource.LicensingApplication" @save-changes="$emit('save-changes', true)" />
  </div>
</template>

<script>
  import ViewOrganizationDetails from '@resources/scripts/patterns/formPatterns/ViewOrganizationDetails.vue';
  import ViewContactDetails from '@resources/scripts/patterns/formPatterns/ViewContactDetails.vue';
  import ViewProjectDetails from '@resources/scripts/patterns/formPatterns/ViewProjectDetails.vue';
  import { SubmissionSource } from '@resources/scripts/models/enums';

  export default {
    components: {
      ViewOrganizationDetails,
      ViewContactDetails,
      ViewProjectDetails,
    },

    props: {
      submissionDetails: {
        type: Object,
        default: () => ({}),
      },
      transactionDetails: {
        type: Object,
        default: () => ({}),
      },
    },

    data() {
      return {
        SubmissionSource, // enum
      };
    },

    computed: {
      computedTransactionDetails: {
        get() {
          return this.transactionDetails;
        },
        set(value) {
          this.$emit('update:transactionDetails', value);
        },
      },

      computedSubmissionDetails: {
        get() {
          return this.submissionDetails;
        },
        set(value) {
          this.$emit('update:submissionDetails', value);
        },
      },

      computedCompany() {
        return this.computedSubmissionDetails.revisedCompany;
      },

      computedPrimaryContact() {
        return this.computedSubmissionDetails.revisedPrimaryContact;
      },

      computedPrincipl1Contact() {
        return this.computedSubmissionDetails.revisedPrincipal1Contact;
      },

      computedPrincipl2Contact() {
        return this.computedSubmissionDetails.revisedPrincipal2Contact;
      },

      computedProject1() {
        return this.computedSubmissionDetails.revisedProject1;
      },

      computedProject2() {
        return this.computedSubmissionDetails.revisedProject2;
      },

      computedProject3() {
        return this.computedSubmissionDetails.revisedProject3;
      },

      computedTransaction() {
        return this.computedSubmissionDetails.transaction;
      },

      computedStandards() {
        return JSON.parse(this.computedSubmissionDetails.revisedStandards);
      },

      computedLicensingSections() {
        return JSON.parse(this.computedSubmissionDetails.revisedLicensingSections);
      },

      computedLineOfBusiness() {
        return JSON.parse(this.computedSubmissionDetails.revisedLineOfBusiness);
      },
    },
  };
</script>